import { useRef, useEffect } from "react";
import { RawBlock } from "editorjs-blocks-react-renderer";
import * as Styled from "./styled";

type Props = {
  html: string;
  variant?: Styled.FrozenHeaderTableVariant;
};

const FrozenHeaderTable = ({ html, variant = "grid" }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const showingRef = useRef(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const overlay = overlayRef.current;
    if (!wrapper || !overlay) return;

    const table = wrapper.querySelector("table");
    const firstRow = wrapper.querySelector("tr");
    if (!table || !firstRow) return;

    const getFixedTopHeight = (): number => {
      const root = document.getElementById("root") ?? document.body;
      let maxBottom = 0;
      const check = (el: Element) => {
        const rect = el.getBoundingClientRect();
        if (
          window.getComputedStyle(el).position === "fixed" &&
          rect.height > 0 &&
          rect.top < window.innerHeight / 2
        ) {
          maxBottom = Math.max(maxBottom, rect.bottom);
        }
      };
      // Only walk 3 levels deep — fixed chrome (header, breadcrumbs) lives close to root
      root.querySelectorAll(":scope > * > * > *, :scope > * > *, :scope > *").forEach(check);
      return maxBottom;
    };

    const headerHeight = getFixedTopHeight();
    overlay.style.top = `${headerHeight}px`;

    // Build overlay: clone of the first row inside a shallow clone of the table (copies all attributes)
    const cloneTable = table.cloneNode(false) as HTMLTableElement;
    const cloneRow = firstRow.cloneNode(true) as HTMLTableRowElement;
    cloneTable.appendChild(cloneRow);
    overlay.innerHTML = "";
    overlay.appendChild(cloneTable);

    const syncLayout = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      overlay.style.left = `${wrapperRect.left}px`;
      overlay.style.width = `${wrapperRect.width}px`;

      const originalCells = Array.from(
        firstRow.querySelectorAll<HTMLElement>("td, th"),
      );
      const clonedCells = Array.from(
        cloneRow.querySelectorAll<HTMLElement>("td, th"),
      );
      originalCells.forEach((cell, i) => {
        const cloned = clonedCells[i];
        if (!cloned) return;
        const { width } = cell.getBoundingClientRect();
        cloned.style.width = `${width}px`;
        cloned.style.minWidth = `${width}px`;
        cloned.style.maxWidth = `${width}px`;
        cloned.style.boxSizing = "border-box";
      });

      cloneTable.style.tableLayout = "fixed";
      cloneTable.style.minWidth = `${table.getBoundingClientRect().width}px`;
      overlay.scrollLeft = wrapper.scrollLeft;
    };

    const rows = Array.from(table.querySelectorAll("tr"));
    const lastRow = rows[rows.length - 1];

    let firstRowAboveHeader = false;
    let lastRowAboveHeader = false;

    const setVisible = (visible: boolean) => {
      showingRef.current = visible;
      overlay.style.visibility = visible ? "visible" : "hidden";
    };

    const updateVisibility = () => {
      // Show when the header row is gone but the table hasn't fully passed the header yet
      const shouldShow = firstRowAboveHeader && !lastRowAboveHeader;
      if (shouldShow && !showingRef.current) syncLayout();
      setVisible(shouldShow);
    };

    // Show when first row scrolls above the fixed chrome
    const firstRowObserver = new IntersectionObserver(
      ([entry]) => {
        firstRowAboveHeader =
          !entry.isIntersecting &&
          entry.boundingClientRect.bottom <= headerHeight;
        updateVisibility();
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 },
    );
    firstRowObserver.observe(firstRow);

    // Hide when the bottom of the last row crosses the bottom edge of the frozen header.
    // topMargin = headerHeight + overlayHeight, so the observer fires when the last row's
    // bottom goes above that line — i.e. the frozen header would overlap content below the table.
    const overlayRowHeight = firstRow.getBoundingClientRect().height;
    const topMargin = headerHeight + overlayRowHeight;
    const lastRowObserver = new IntersectionObserver(
      ([entry]) => {
        lastRowAboveHeader =
          !entry.isIntersecting &&
          entry.boundingClientRect.bottom < topMargin;
        updateVisibility();
      },
      { rootMargin: `-${topMargin}px 0px 0px 0px`, threshold: 0 },
    );
    lastRowObserver.observe(lastRow);

    const handleScroll = () => {
      overlay.scrollLeft = wrapper.scrollLeft;
    };
    wrapper.addEventListener("scroll", handleScroll);

    const handleResize = () => {
      if (showingRef.current) syncLayout();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      firstRowObserver.disconnect();
      lastRowObserver.disconnect();
      wrapper.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [html, variant]);

  return (
    <>
      <Styled.StickyHeaderOverlay ref={overlayRef} $variant={variant} />
      <Styled.DatoTable ref={wrapperRef} $variant={variant}>
        <RawBlock data={{ html }} />
      </Styled.DatoTable>
    </>
  );
};

export default FrozenHeaderTable;
